<template>
  <div class="form-head">
    <center><label class="text-lg">Payment Form</label></center>
    <div class="form-body">
      <div class="form_container">
        <form class="text-md" @submit.prevent>
          <label class="text-lg">Bank Name</label>
          <soft-input
            id="bank_name"
            type="text"
            placeholder="Bank Name"
            name="bank_name"
            size="lg"
            v-model="formData.bank_name"
          />

          <label class="text-lg">Account Holder</label>
          <soft-input
            id="account_name"
            type="text"
            placeholder="Account Holder Name"
            name="account_name"
            size="lg"
            v-model="formData.account_name"
          />

          <label class="text-lg">Account Type</label>
          <soft-select
            id="account_type"
            name="account_type"
            v-model="formData.account_type"
            :options="[
              { label: 'Savings Account', value: 'Savings Account' },
              { label: 'Checking Account', value: 'Checking Account' },
            ]"
          />

          <label class="text-lg">Routing Number</label>
          <soft-input
            id="routing_number"
            type="text"
            inputmode="numeric"
            name="routing_number"
            size="lg"
            v-model="formData.routing_number"
          />

          <label class="text-lg">Account Number</label>
          <soft-input
            id="account_number"
            type="text"
            inputmode="numeric"
            name="account_number"
            size="lg"
            v-model="formData.account_number"
          />

          <label class="text-lg">Sample Voided Check (Image or PDF)</label>
          <input
            type="file"
            accept="image/*,.pdf"
            @change="handleFileUpload"
            class="border rounded w-full px-3 py-2 mb-3"
          />

          <div
            v-if="formData.sample_void_check"
            class="text-sm text-blueGray-600 mb-0"
          >
            Selected file: {{ formData.sample_void_check.name }}
          </div>

          <!-- View Button -->
          <div v-if="formData.has_uploaded_check" class="mb-3">
            <soft-button
              color="info"
              size="md"
              type="button"
              @click="openFilePreview"
            >
              View Uploaded File
            </soft-button>
          </div>

          <!-- File Preview Modal -->
          <!-- File Preview Modal -->
          <transition name="fade">
            <div v-if="showFileModal" class="modal-overlay">
              <div class="modal-container">
                <button @click="closeFileModal" class="close-btn">
                  &times;
                </button>
                <h3 class="text-xl font-semibold mb-4 text-center">
                  Uploaded Voided Check
                </h3>

                <div class="preview-area">
                  <template v-if="fileType === 'image'">
                    <img
                      :src="fileBlobUrl"
                      alt="Uploaded Voided Check"
                      class="w-full h-auto object-contain rounded-lg"
                    />
                  </template>

                  <template v-else-if="fileType === 'pdf'">
                    <iframe
                      :src="fileBlobUrl"
                      class="w-full h-[780px] rounded-lg"
                      frameborder="0"
                    ></iframe>
                  </template>

                  <template v-else>
                    <p class="text-center text-gray-500">
                      Unable to preview this file type.
                    </p>
                  </template>
                </div>
              </div>
            </div>
          </transition>

          <soft-button
            color="success"
            size="lg"
            type="button"
            fullWidth="true"
            @click="create_payment_details"
          >
            Submit
          </soft-button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import SoftInput from "@/components/SoftInput.vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftSelect from "@/components/SoftSelect.vue";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  components: { SoftInput, SoftButton, SoftSelect },

  data() {
    return {
      formData: {
        bank_name: "",
        account_number: "",
        routing_number: "",
        account_type: "Savings Account",
        account_name: "",
        sample_void_check: null,
        has_uploaded_check: false,
      },
      showFileModal: false,
      fileBlobUrl: null,
      fileType: null,
      s3FileUrl: null, // internal secure reference
    };
  },

  methods: {
    async create_payment_details() {
      try {
        const formData = new FormData();
        Object.entries(this.formData).forEach(([key, val]) => {
          if (val && key !== "has_uploaded_check") formData.append(key, val);
        });

        Swal.fire({
          title: "Uploading...",
          text: "Please wait.",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        const res = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/create_bank_details`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        Swal.fire("Success", "Payment details saved successfully", "success");
        this.$emit("payment-form-updated");
        this.formData = res.data.bank_details;
        this.formData.has_uploaded_check = !!this.formData.void_check_url;
        this.s3FileUrl = this.formData.void_check_url;
      } catch (err) {
        console.error(err);
        if (err.response?.data?.error) {
          const messages = Object.values(err.response.data.error)
            .flat()
            .join("\n");
          Swal.fire("Validation Error", messages, "error");
        } else {
          Swal.fire("Error", "Failed to save payment details.", "error");
        }
      }
    },

    async fetch_payment_details() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_bank_details`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (res.data.bank_details) {
          this.formData = res.data.bank_details;
          this.formData.has_uploaded_check = !!this.formData.void_check_url;
          this.s3FileUrl = this.formData.void_check_url;
        }
      } catch (error) {
        console.error("Error fetching payment details:", error);
      }
    },

    async openFilePreview() {
      if (!this.s3FileUrl) {
        Swal.fire("No File", "No voided check uploaded.", "info");
        return;
      }

      Swal.fire({
        title: "Loading File...",
        text: "Please wait.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const res = await fetch(this.s3FileUrl);
        const blob = await res.blob();
        Swal.close();

        const type = blob.type;
        this.fileBlobUrl = URL.createObjectURL(blob);
        this.fileType = type.includes("pdf")
          ? "pdf"
          : type.includes("image")
          ? "image"
          : "other";

        this.showFileModal = true;
      } catch (err) {
        Swal.close();
        console.error(err);
        Swal.fire("Error", "Unable to preview file.", "error");
      }
    },

    closeFileModal() {
      this.showFileModal = false;
      if (this.fileBlobUrl) {
        URL.revokeObjectURL(this.fileBlobUrl);
        this.fileBlobUrl = null;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowed = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowed.includes(file.type)) {
        Swal.fire(
          "Invalid File",
          "Only JPG, PNG, or PDF files allowed.",
          "error"
        );
        event.target.value = "";
        return;
      }
      this.formData.sample_void_check = file;
    },
  },

  mounted() {
    this.fetch_payment_details();
  },
};
</script>
<style scoped>
/* Modal transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  padding: 1rem;
}

/* Modal container */
.modal-container {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.4);
  animation: pop-in 0.25s ease-out;
}

/* Pop-in animation */
@keyframes pop-in {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Close button */
.close-btn {
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  font-size: 2rem;
  color: #555;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #000;
}

/* Preview area */
.preview-area {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem;
  max-height: 80vh;
  overflow-y: auto;
}
</style>
